import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Switch } from './ui/switch';
import {
  Wrench,
  Plus,
  Play,
  Edit,
  Trash2,
  Video,
  FileText,
  Clock,
  AlertTriangle,
  Shield,
  CheckCircle,
  MapPin,
  Link as LinkIcon,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  HardHat,
  Cog,
} from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['Preventive', 'Corrective', 'Predictive', 'Emergency', 'Inspection'];
const FREQUENCIES = ['Daily', 'Weekly', 'Monthly', 'Quarterly', 'Annual', 'As-Needed'];
const PRIORITIES = ['Critical', 'High', 'Medium', 'Low'];

const getPriorityColor = (priority) => {
  switch (priority) {
    case 'Critical': return 'bg-red-500/20 text-red-400 border-red-500/30';
    case 'High': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
    case 'Medium': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    case 'Low': return 'bg-green-500/20 text-green-400 border-green-500/30';
    default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  }
};

const getCategoryIcon = (category) => {
  switch (category) {
    case 'Preventive': return <Shield className="h-4 w-4" />;
    case 'Corrective': return <Wrench className="h-4 w-4" />;
    case 'Emergency': return <AlertTriangle className="h-4 w-4" />;
    case 'Inspection': return <CheckCircle className="h-4 w-4" />;
    default: return <Cog className="h-4 w-4" />;
  }
};

// Video embed helper
const getEmbedUrl = (url) => {
  if (!url) return null;
  const youtubeMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (youtubeMatch) return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  return url;
};

export function MaintenanceProceduresPanel({
  isOpen,
  onClose,
  currentTour,
  currentSweep,
  cameraPosition,
  onNavigate,
  equipment = [],
}) {
  const { canEdit } = useAuth();
  const [procedures, setProcedures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProcedure, setSelectedProcedure] = useState(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [expandedSteps, setExpandedSteps] = useState({});
  
  const [formData, setFormData] = useState({
    title: '',
    procedure_number: '',
    description: '',
    category: 'Preventive',
    frequency: 'As-Needed',
    priority: 'Medium',
    equipment_id: '',
    equipment_name: '',
    steps: [],
    tools_required: [],
    ppe_required: [],
    safety_precautions: [],
    lockout_tagout_required: false,
    estimated_duration_minutes: '',
    video_url: '',
    document_url: '',
    embed_video: true,
    sweep_id: '',
    rotation: null,
    zoom: null,
  });

  const token = localStorage.getItem('zentara_token');

  const fetchProcedures = useCallback(async () => {
    if (!currentTour?.id) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/maintenance-procedures?tour_id=${currentTour.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setProcedures(data);
      }
    } catch (err) {
      console.error('Failed to fetch maintenance procedures:', err);
    } finally {
      setLoading(false);
    }
  }, [currentTour?.id, token]);

  useEffect(() => {
    if (isOpen) {
      fetchProcedures();
    }
  }, [isOpen, fetchProcedures]);

  const handleCreate = async () => {
    try {
      const payload = {
        ...formData,
        tour_id: currentTour.id,
        estimated_duration_minutes: formData.estimated_duration_minutes ? parseInt(formData.estimated_duration_minutes) : null,
      };
      
      const res = await fetch(`${API_URL}/api/maintenance-procedures`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        fetchProcedures();
        setIsCreateDialogOpen(false);
        resetForm();
      }
    } catch (err) {
      console.error('Failed to create maintenance procedure:', err);
    }
  };

  const handleUpdate = async () => {
    if (!selectedProcedure) return;
    try {
      const payload = {
        ...formData,
        estimated_duration_minutes: formData.estimated_duration_minutes ? parseInt(formData.estimated_duration_minutes) : null,
      };
      
      const res = await fetch(`${API_URL}/api/maintenance-procedures/${selectedProcedure.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        fetchProcedures();
        setIsEditDialogOpen(false);
        setSelectedProcedure(null);
        resetForm();
      }
    } catch (err) {
      console.error('Failed to update maintenance procedure:', err);
    }
  };

  const handleDelete = async (procedureId) => {
    if (!window.confirm('Are you sure you want to delete this maintenance procedure?')) return;
    try {
      const res = await fetch(`${API_URL}/api/maintenance-procedures/${procedureId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        fetchProcedures();
      }
    } catch (err) {
      console.error('Failed to delete maintenance procedure:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      procedure_number: '',
      description: '',
      category: 'Preventive',
      frequency: 'As-Needed',
      priority: 'Medium',
      equipment_id: '',
      equipment_name: '',
      steps: [],
      tools_required: [],
      ppe_required: [],
      safety_precautions: [],
      lockout_tagout_required: false,
      estimated_duration_minutes: '',
      video_url: '',
      document_url: '',
      embed_video: true,
      sweep_id: '',
      rotation: null,
      zoom: null,
    });
  };

  const captureCurrentPosition = () => {
    if (currentSweep?.id) {
      setFormData(prev => ({
        ...prev,
        sweep_id: currentSweep.id,
        rotation: cameraPosition?.rotation || null,
        zoom: cameraPosition?.zoom || null,
      }));
    }
  };

  const openEditDialog = (procedure) => {
    setSelectedProcedure(procedure);
    setFormData({
      title: procedure.title || '',
      procedure_number: procedure.procedure_number || '',
      description: procedure.description || '',
      category: procedure.category || 'Preventive',
      frequency: procedure.frequency || 'As-Needed',
      priority: procedure.priority || 'Medium',
      equipment_id: procedure.equipment_id || '',
      equipment_name: procedure.equipment_name || '',
      steps: procedure.steps || [],
      tools_required: procedure.tools_required || [],
      ppe_required: procedure.ppe_required || [],
      safety_precautions: procedure.safety_precautions || [],
      lockout_tagout_required: procedure.lockout_tagout_required || false,
      estimated_duration_minutes: procedure.estimated_duration_minutes?.toString() || '',
      video_url: procedure.video_url || '',
      document_url: procedure.document_url || '',
      embed_video: procedure.embed_video ?? true,
      sweep_id: procedure.sweep_id || '',
      rotation: procedure.rotation || null,
      zoom: procedure.zoom || null,
    });
    setIsEditDialogOpen(true);
  };

  const openViewDialog = (procedure) => {
    setSelectedProcedure(procedure);
    setExpandedSteps({});
    setIsViewDialogOpen(true);
  };

  const navigateToProcedure = (procedure) => {
    if (procedure.sweep_id && onNavigate) {
      onNavigate(procedure.sweep_id, { 
        rotation: procedure.rotation,
        zoom: procedure.zoom 
      });
    }
  };

  const filteredProcedures = activeTab === 'all' 
    ? procedures 
    : procedures.filter(p => p.category === activeTab);

  const addStep = () => {
    setFormData(prev => ({
      ...prev,
      steps: [...prev.steps, {
        step_number: prev.steps.length + 1,
        instruction: '',
        details: '',
        warning: '',
        tools_required: [],
        estimated_time_minutes: null,
      }]
    }));
  };

  const updateStep = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.map((step, i) => 
        i === index ? { ...step, [field]: value } : step
      )
    }));
  };

  const removeStep = (index) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index).map((step, i) => ({
        ...step,
        step_number: i + 1
      }))
    }));
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5 text-primary" />
            Maintenance Procedures
          </DialogTitle>
          <DialogDescription>
            Step-by-step maintenance guides linked to equipment and locations
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Category Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full justify-start overflow-x-auto">
              <TabsTrigger value="all">All</TabsTrigger>
              {CATEGORIES.map(cat => (
                <TabsTrigger key={cat} value={cat}>{cat}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {/* Procedure List */}
          <ScrollArea className="flex-1 mt-4">
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : filteredProcedures.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No maintenance procedures found
              </div>
            ) : (
              <div className="grid gap-4 pr-4">
                {filteredProcedures.map(procedure => (
                  <Card key={procedure.id} className="hover:border-primary/50 transition-colors">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          {getCategoryIcon(procedure.category)}
                          <div>
                            <CardTitle className="text-lg">{procedure.title}</CardTitle>
                            {procedure.procedure_number && (
                              <span className="text-xs text-muted-foreground">{procedure.procedure_number}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={getPriorityColor(procedure.priority)}>
                            {procedure.priority}
                          </Badge>
                          <Badge variant="outline">{procedure.frequency}</Badge>
                        </div>
                      </div>
                      {procedure.description && (
                        <CardDescription className="mt-1">{procedure.description}</CardDescription>
                      )}
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          {procedure.equipment_name && (
                            <span className="flex items-center gap-1">
                              <Cog className="h-3 w-3" />
                              {procedure.equipment_name}
                            </span>
                          )}
                          {procedure.estimated_duration_minutes && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {procedure.estimated_duration_minutes} min
                            </span>
                          )}
                          {procedure.lockout_tagout_required && (
                            <span className="flex items-center gap-1 text-yellow-500">
                              <AlertTriangle className="h-3 w-3" />
                              LOTO Required
                            </span>
                          )}
                          {procedure.steps?.length > 0 && (
                            <span>{procedure.steps.length} steps</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {procedure.sweep_id && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => navigateToProcedure(procedure)}
                            >
                              <MapPin className="h-4 w-4 mr-1" />
                              Go to
                            </Button>
                          )}
                          <Button 
                            variant="default" 
                            size="sm"
                            onClick={() => openViewDialog(procedure)}
                          >
                            <Play className="h-4 w-4 mr-1" />
                            View
                          </Button>
                          {canEdit && (
                            <>
                              <Button 
                                variant="ghost" 
                                size="icon-sm"
                                onClick={() => openEditDialog(procedure)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon-sm"
                                onClick={() => handleDelete(procedure.id)}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Footer with Add Button */}
        {canEdit && (
          <DialogFooter>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Procedure
            </Button>
          </DialogFooter>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={isCreateDialogOpen || isEditDialogOpen} onOpenChange={(open) => {
          if (!open) {
            setIsCreateDialogOpen(false);
            setIsEditDialogOpen(false);
            setSelectedProcedure(null);
            resetForm();
          }
        }}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {isEditDialogOpen ? 'Edit Maintenance Procedure' : 'Create Maintenance Procedure'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Title</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Procedure title"
                  />
                </div>

                <div>
                  <Label>Procedure Number</Label>
                  <Input
                    value={formData.procedure_number}
                    onChange={(e) => setFormData(prev => ({ ...prev, procedure_number: e.target.value }))}
                    placeholder="e.g., MP-2024-001"
                  />
                </div>

                <div>
                  <Label>Linked Equipment</Label>
                  <Select
                    value={formData.equipment_id}
                    onValueChange={(value) => {
                      const equip = equipment.find(e => e.id === value);
                      setFormData(prev => ({ 
                        ...prev, 
                        equipment_id: value,
                        equipment_name: equip?.name || ''
                      }));
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select equipment (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">None</SelectItem>
                      {equipment.map(equip => (
                        <SelectItem key={equip.id} value={equip.id}>{equip.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="col-span-2">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description"
                  />
                </div>

                <div>
                  <Label>Category</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Frequency</Label>
                  <Select
                    value={formData.frequency}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, frequency: value }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FREQUENCIES.map(freq => (
                        <SelectItem key={freq} value={freq}>{freq}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Priority</Label>
                  <Select
                    value={formData.priority}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, priority: value }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map(p => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Est. Duration (min)</Label>
                  <Input
                    type="number"
                    value={formData.estimated_duration_minutes}
                    onChange={(e) => setFormData(prev => ({ ...prev, estimated_duration_minutes: e.target.value }))}
                  />
                </div>

                <div className="col-span-2 flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.lockout_tagout_required}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, lockout_tagout_required: checked }))}
                    />
                    <Label>Lockout/Tagout Required</Label>
                  </div>
                </div>

                <div className="col-span-2">
                  <Label>Video URL (YouTube/Vimeo)</Label>
                  <Input
                    value={formData.video_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, video_url: e.target.value }))}
                    placeholder="https://youtube.com/watch?v=..."
                  />
                </div>

                <div className="col-span-2">
                  <Label>Location in Tour</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      value={formData.sweep_id || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, sweep_id: e.target.value }))}
                      placeholder="Sweep ID (optional)"
                      className="flex-1"
                    />
                    <Button variant="outline" onClick={captureCurrentPosition}>
                      <MapPin className="h-4 w-4 mr-1" />
                      Capture Current
                    </Button>
                  </div>
                </div>

                {/* Procedure Steps */}
                <div className="col-span-2">
                  <div className="flex items-center justify-between mb-2">
                    <Label>Procedure Steps</Label>
                    <Button variant="outline" size="sm" onClick={addStep}>
                      <Plus className="h-4 w-4 mr-1" />
                      Add Step
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {formData.steps.map((step, idx) => (
                      <div key={idx} className="p-3 border rounded-lg space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">Step {step.step_number}</span>
                          <Button variant="ghost" size="icon-sm" onClick={() => removeStep(idx)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                        <Input
                          value={step.instruction}
                          onChange={(e) => updateStep(idx, 'instruction', e.target.value)}
                          placeholder="Main instruction"
                        />
                        <Textarea
                          value={step.details || ''}
                          onChange={(e) => updateStep(idx, 'details', e.target.value)}
                          placeholder="Additional details (optional)"
                          className="text-sm"
                        />
                        <Input
                          value={step.warning || ''}
                          onChange={(e) => updateStep(idx, 'warning', e.target.value)}
                          placeholder="Safety warning (optional)"
                          className="text-sm border-yellow-500/30"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setIsCreateDialogOpen(false);
                setIsEditDialogOpen(false);
                resetForm();
              }}>
                Cancel
              </Button>
              <Button onClick={isEditDialogOpen ? handleUpdate : handleCreate}>
                {isEditDialogOpen ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Procedure Dialog */}
        <Dialog open={isViewDialogOpen} onOpenChange={(open) => {
          if (!open) {
            setIsViewDialogOpen(false);
            setSelectedProcedure(null);
          }
        }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle className="flex items-center gap-2">
                  {getCategoryIcon(selectedProcedure?.category)}
                  {selectedProcedure?.title}
                </DialogTitle>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={getPriorityColor(selectedProcedure?.priority)}>
                    {selectedProcedure?.priority}
                  </Badge>
                  <Badge variant="outline">{selectedProcedure?.frequency}</Badge>
                </div>
              </div>
              {selectedProcedure?.procedure_number && (
                <span className="text-sm text-muted-foreground">{selectedProcedure.procedure_number}</span>
              )}
              {selectedProcedure?.description && (
                <DialogDescription>{selectedProcedure.description}</DialogDescription>
              )}
            </DialogHeader>

            {/* Safety Warnings */}
            {selectedProcedure?.lockout_tagout_required && (
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-yellow-500" />
                <span className="font-medium text-yellow-500">Lockout/Tagout (LOTO) Required</span>
              </div>
            )}

            {/* PPE Requirements */}
            {selectedProcedure?.ppe_required?.length > 0 && (
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <HardHat className="h-4 w-4 text-blue-400" />
                  <span className="font-medium text-blue-400">Required PPE</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedProcedure.ppe_required.map((ppe, idx) => (
                    <Badge key={idx} variant="outline">{ppe}</Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Video Embed */}
            {selectedProcedure?.video_url && selectedProcedure?.embed_video && (
              <div className="aspect-video w-full bg-black rounded-lg overflow-hidden">
                <iframe
                  src={getEmbedUrl(selectedProcedure.video_url)}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={selectedProcedure.title}
                />
              </div>
            )}

            {/* Procedure Steps */}
            {selectedProcedure?.steps?.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium">Procedure Steps</h4>
                {selectedProcedure.steps.map((step, idx) => (
                  <Collapsible
                    key={idx}
                    open={expandedSteps[idx]}
                    onOpenChange={(open) => setExpandedSteps(prev => ({ ...prev, [idx]: open }))}
                  >
                    <div className="border rounded-lg">
                      <CollapsibleTrigger className="w-full p-3 flex items-center justify-between hover:bg-muted/50">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm font-medium">
                            {step.step_number}
                          </div>
                          <span className="font-medium">{step.instruction}</span>
                        </div>
                        {expandedSteps[idx] ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="px-3 pb-3 pl-14 space-y-2">
                          {step.details && (
                            <p className="text-sm text-muted-foreground">{step.details}</p>
                          )}
                          {step.warning && (
                            <div className="flex items-center gap-2 text-sm text-yellow-500">
                              <AlertTriangle className="h-4 w-4" />
                              {step.warning}
                            </div>
                          )}
                          {step.tools_required?.length > 0 && (
                            <div className="flex items-center gap-2 text-sm">
                              <Wrench className="h-4 w-4" />
                              Tools: {step.tools_required.join(', ')}
                            </div>
                          )}
                          {step.estimated_time_minutes && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Clock className="h-4 w-4" />
                              ~{step.estimated_time_minutes} minutes
                            </div>
                          )}
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                ))}
              </div>
            )}

            <DialogFooter>
              {selectedProcedure?.sweep_id && (
                <Button variant="outline" onClick={() => {
                  navigateToProcedure(selectedProcedure);
                  setIsViewDialogOpen(false);
                }}>
                  <MapPin className="h-4 w-4 mr-2" />
                  Go to Location
                </Button>
              )}
              <Button onClick={() => setIsViewDialogOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

export default MaintenanceProceduresPanel;
